import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

interface Product {
  id: number;
  name: string;
  ean?: string;
  gross_weight?: number;
  cbm?: number;
  pieces?: number;
  cartons?: number;
}

interface ProductSummary {
  total_cbm: number;
  total_cartons: number;
  total_weight: number;
  total_pieces: number;
  product_count: number;
}

// Define the PickupRequest interface based on what's used in PricingCalculator.tsx
interface Booking {
  id: number;
  created_at: string;
  status: string;
  eta: string;
  reference: string;
  rejection_reason?: string | null;
  origin_pickup_date: string | null;
  transport_type: {
    name: string;
    abbreviation: string;
  };
  booking_type?: {
    name: string;
  };
  booking_incoterm: {
    name: string;
  };
  origin_port: {
    name: string;
    country: string | null;
  };
  destination_port: {
    name: string;
    country: string | null;
  };
  container?: {
    reference: string | null;
    eta: string | null;
    etd: string | null;
    delayed_etd?: string | null;
    eta_customer: string | null;
    status: string;
    track_and_trace: string | null;
    origin_port?: {
      name: string;
      country: string | null;
    };
    destination_port?: {
      name: string;
      country: string | null;
    };
  };
  supplier?: {
    name: string;
  };
  products?: Product[];
  products_summary?: {
    total_cbm: number;
    total_cartons: number;
    total_weight: number;
    total_pieces: number;
    product_count: number;
  };
  pickup_request?: Array<{
    status: string;
    cost?: number;
    created_at: string;
    notes?: string;
  }>;
  delivery_type?: "storage" | "address";
  delivery_details?: Array<{
    country: string;
    city: string;
    postal_code: string;
    street_name: string;
    house_number: string;
    instructions: string | null;
  }>;
}

export const useBookings = (companyId: number | null, includeArchived: boolean = false) => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBookings = async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('bookings')
        .select(`
          *,
          transport_type:booking_transport_types(name, abbreviation),
          booking_type:bookings_type(name),
          booking_incoterm:bookings_incoterm(name),
          origin_port:booking_origin_port(name, country),
          destination_port:booking_destination_port(name, country),
          supplier:booking_supplier(name),
          container:containers(
            reference, 
            eta, 
            etd,
            delayed_etd,
            eta_customer,
            status, 
            track_and_trace,
            origin_port:booking_origin_port(name, country),
            destination_port:booking_destination_port(name, country)
          ),
          delivery_details:booking_delivery(
            country,
            city,
            postal_code,
            street_name,
            house_number,
            instructions
          ),
          products:booking_product(
            id,
            name,
            ean,
            gross_weight,
            cbm,
            pieces,
            cartons
          )
        `)
        .eq('company_id', companyId);

      // Only include non-archived bookings unless specifically requested
      if (!includeArchived) {
        query = query.neq('status', 'archived');
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      
      // Calculate product summaries for each booking
      const bookingsWithSummaries = (data || []).map((booking) => {
        const products = booking.products || [];
        const products_summary = products.reduce(
          (acc: ProductSummary, product: Product) => ({
            total_cbm: acc.total_cbm + (product.cbm || 0),
            total_cartons: acc.total_cartons + (product.cartons || 0),
            total_weight: acc.total_weight + (product.gross_weight || 0),
            total_pieces: acc.total_pieces + (product.pieces || 0),
            product_count: acc.product_count + 1,
          }),
          {
            total_cbm: 0,
            total_cartons: 0,
            total_weight: 0,
            total_pieces: 0,
            product_count: 0,
          }
        );

        return {
          ...booking,
          products_summary,
        };
      });
      
      setBookings(bookingsWithSummaries || []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch bookings';
      setError(message);
      console.error('Error fetching bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  const archiveBooking = async (id: number) => {
    const { error } = await supabase
      .from('bookings')
      .update({ status: 'archived' })
      .eq('id', id);

    if (error) throw error;
  };

  const deleteBooking = async (id: number) => {
    return archiveBooking(id);
  };

  useEffect(() => {
    fetchBookings();
  }, [companyId]);

  return { bookings, loading, error, archiveBooking, deleteBooking, refetch: fetchBookings };
}; 